import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ListChecks, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useCustomers } from '@/hooks/useCustomers';
import { formatDateTime } from '@/lib/format';
import type { ActiveContractStatus, Customer } from '@/types/api';

/**
 * Wartungsplan-Übersicht (Sprint 3 Sidebar-Erweiterung).
 *
 * Listet alle Kunden mit Stats. Da `?expand=stats` `active_contract_status`
 * (= "active"/"missing"/...) liefert, kann man hier die Kunden ohne
 * Vertrags-Mapping sofort sehen. Klick führt auf customers/:id?tab=plan.
 */

export const Route = createFileRoute('/maintenance-plans/')({
  component: MaintenancePlansOverviewPage,
});

function MaintenancePlansOverviewPage(): JSX.Element {
  const customersQuery = useCustomers({ expand: 'stats', limit: 200 });
  const customers = useMemo(
    () => customersQuery.data?.data ?? [],
    [customersQuery.data]
  );

  const grouped = useMemo(() => splitByContract(customers), [customers]);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Wartungspläne</h1>
        <p className="text-muted">
          Pro Kunde wird aus dem Vertrags-Service-Profil (oder fallback auf die
          Systeme) ein Plan generiert. Klick auf den Kunden führt direkt zum
          Plan-Tab.
        </p>
      </header>

      {customersQuery.isLoading && <SkeletonRows rows={5} cols={3} />}
      {customersQuery.isError && <ErrorAlert error={customersQuery.error} />}

      {!customersQuery.isLoading && customers.length === 0 && (
        <Card>
          <EmptyState
            icon={<ListChecks className="size-8" />}
            title="Keine Kunden vorhanden"
            description="Importiere zuerst die ods-Tabelle oder lege Kunden manuell an."
            actions={
              <Link to="/settings/import">
                <Button>Zum Import</Button>
              </Link>
            }
          />
        </Card>
      )}

      {customers.length > 0 && (
        <CustomerPlanTable
          title="Mit aktivem Vertrag"
          customers={grouped.withContract}
          emptyHint="Keine Kunden mit aktivem Vertrag – Mapping prüfen."
        />
      )}
      {grouped.withoutContract.length > 0 && (
        <CustomerPlanTable
          title="Ohne Vertrags-Mapping"
          customers={grouped.withoutContract}
          emptyHint=""
          highlight="warning"
        />
      )}
    </div>
  );
}

function splitByContract(customers: Customer[]): {
  withContract: Customer[];
  withoutContract: Customer[];
} {
  const withContract: Customer[] = [];
  const withoutContract: Customer[] = [];
  for (const c of customers) {
    const status = c.stats?.active_contract_status;
    if (status === 'mapped') withContract.push(c);
    else withoutContract.push(c);
  }
  return { withContract, withoutContract };
}

function CustomerPlanTable({
  title,
  customers,
  emptyHint,
  highlight,
}: {
  title: string;
  customers: Customer[];
  emptyHint: string;
  highlight?: 'warning';
}): JSX.Element {
  if (customers.length === 0) {
    return (
      <Card title={title}>
        <p className="text-sm text-muted">{emptyHint}</p>
      </Card>
    );
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>{title}</span>
          <Badge tone={highlight === 'warning' ? 'warning' : 'neutral'}>
            {customers.length}
          </Badge>
        </span>
      }
    >
      <DataTable
        caption={title}
        columns={PLAN_COLUMNS}
        rows={customers}
        rowKey={(c) => c.id}
        storageKey="wartungsplaene"
      />
    </Card>
  );
}

function ContractBadge({
  status,
}: {
  status: ActiveContractStatus | null | undefined;
}): JSX.Element {
  if (status === 'mapped') return <Badge tone="success">aktiv</Badge>;
  if (status === 'unmapped') return <Badge tone="warning">kein Mapping</Badge>;
  if (status === 'none' || status == null) return <Badge tone="neutral">–</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

function vertragFiltertext(status: ActiveContractStatus | null | undefined): string {
  if (status === 'mapped') return 'aktiv';
  if (status === 'unmapped') return 'kein Mapping';
  if (status === 'none' || status == null) return '';
  return status;
}

const PLAN_COLUMNS: ReadonlyArray<Column<Customer>> = [
  {
    id: 'kunde',
    header: 'Kunde',
    filterValue: (c) => `${c.name} ${c.slug}`,
    cell: (c) => (
      <>
        <Link
          to="/customers/$id"
          params={{ id: c.id }}
          search={{ tab: 'plan' }}
          className="text-primary hover:underline"
        >
          {c.name}
        </Link>
        <p className="font-mono text-xs text-muted">{c.slug}</p>
      </>
    ),
  },
  {
    id: 'vertrag',
    header: 'Vertrag',
    filter: 'select',
    filterValue: (c) => vertragFiltertext(c.stats?.active_contract_status),
    cell: (c) => <ContractBadge status={c.stats?.active_contract_status} />,
  },
  {
    id: 'systeme',
    header: 'Systeme',
    className: 'text-xs',
    filterValue: (c) => String(c.stats?.systems_count ?? 0),
    cell: (c) => c.stats?.systems_count ?? 0,
  },
  {
    id: 'letzte-wartung',
    header: 'Letzte Wartung',
    className: 'text-xs',
    filterValue: (c) => ohneStrich(formatDateTime(c.stats?.last_maintenance_at ?? null)),
    cell: (c) => formatDateTime(c.stats?.last_maintenance_at ?? null),
  },
  {
    id: 'aktion',
    header: 'Aktion',
    filter: false,
    headerClassName: 'text-right',
    className: 'text-right',
    cell: (c) => (
      <Link to="/customers/$id" params={{ id: c.id }} search={{ tab: 'plan' }}>
        <Button size="sm" variant="ghost" leftIcon={<Sparkles className="size-3" />}>
          Plan öffnen
        </Button>
      </Link>
    ),
  },
];
